import type { UserRole } from "./RoleContext";

/**
 * Permission categories/modules
 */
export type PermissionModule =
  | "leads"
  | "opportunities"
  | "jobs"
  | "customers"
  | "contractors"
  | "inventory"
  | "warehouse"
  | "ordering"
  | "permitting"
  | "issues"
  | "accounting"
  | "reports"
  | "settings"
  | "admin"
  | "catalog"
  | "vendors"
  | "messages"
  | "automations"
  | "qa"
  | "installation"
  | "scheduling"
  | "geo_tracking";

/**
 * Granular permission actions
 */
export type PermissionAction = "view" | "create" | "edit" | "delete" | "approve" | "export";

/**
 * Individual permission key format: module_action (e.g., "jobs_view", "jobs_create")
 * Using string for flexibility
 */
export type PermissionKey = string;

/**
 * Permission definition with metadata
 */
export interface PermissionDefinition {
  key: PermissionKey;
  module: PermissionModule;
  action: PermissionAction;
  label: string;
  description: string;
  route?: string;
}

/**
 * Role-based permission assignment
 */
export interface RolePermission {
  role: UserRole;
  permissions: PermissionKey[];
}

/**
 * All permission definitions indexed by key
 */
export const PERMISSIONS: Record<string, PermissionDefinition> = {
  // Leads Module
  leads_view: { key: "leads_view", module: "leads", action: "view", label: "View Leads", description: "View lead list and details", route: "/pipeline" },
  leads_create: { key: "leads_create", module: "leads", action: "create", label: "Create Leads", description: "Create new leads", route: "/pipeline" },
  leads_edit: { key: "leads_edit", module: "leads", action: "edit", label: "Edit Leads", description: "Edit lead information", route: "/pipeline/:id" },
  leads_delete: { key: "leads_delete", module: "leads", action: "delete", label: "Delete Leads", description: "Delete leads", route: "/pipeline" },
  leads_export: { key: "leads_export", module: "leads", action: "export", label: "Export Leads", description: "Export lead data", route: "/pipeline" },

  // Opportunities Module
  opportunities_view: { key: "opportunities_view", module: "opportunities", action: "view", label: "View Opportunities", description: "View opportunity list and details", route: "/opportunities" },
  opportunities_create: { key: "opportunities_create", module: "opportunities", action: "create", label: "Create Opportunities", description: "Create new opportunities", route: "/opportunities" },
  opportunities_edit: { key: "opportunities_edit", module: "opportunities", action: "edit", label: "Edit Opportunities", description: "Edit opportunity information", route: "/opportunities/:id" },
  opportunities_delete: { key: "opportunities_delete", module: "opportunities", action: "delete", label: "Delete Opportunities", description: "Delete opportunities", route: "/opportunities" },
  opportunities_approve: { key: "opportunities_approve", module: "opportunities", action: "approve", label: "Approve Opportunities", description: "Approve opportunity decisions", route: "/opportunities/:id" },

  // Jobs Module
  jobs_view: { key: "jobs_view", module: "jobs", action: "view", label: "View Jobs", description: "View job list and details", route: "/jobs" },
  jobs_create: { key: "jobs_create", module: "jobs", action: "create", label: "Create Jobs", description: "Create new jobs", route: "/jobs" },
  jobs_edit: { key: "jobs_edit", module: "jobs", action: "edit", label: "Edit Jobs", description: "Edit job information", route: "/jobs/:id" },
  jobs_delete: { key: "jobs_delete", module: "jobs", action: "delete", label: "Delete Jobs", description: "Delete jobs", route: "/jobs" },
  jobs_approve: { key: "jobs_approve", module: "jobs", action: "approve", label: "Approve Jobs", description: "Approve job decisions", route: "/jobs/:id" },

  // Customers Module
  customers_view: { key: "customers_view", module: "customers", action: "view", label: "View Customers", description: "View customer list and details", route: "/customers" },
  customers_create: { key: "customers_create", module: "customers", action: "create", label: "Create Customers", description: "Create new customers", route: "/customers" },
  customers_edit: { key: "customers_edit", module: "customers", action: "edit", label: "Edit Customers", description: "Edit customer information", route: "/customers/:name" },
  customers_delete: { key: "customers_delete", module: "customers", action: "delete", label: "Delete Customers", description: "Delete customers", route: "/customers" },
  customers_export: { key: "customers_export", module: "customers", action: "export", label: "Export Customers", description: "Export customer data", route: "/customers" },

  // Inventory additional
  inventory_approve: { key: "inventory_approve", module: "inventory", action: "approve", label: "Approve Inventory", description: "Approve inventory requests", route: "/inventory" },

  // Warehouse additional
  warehouse_approve: { key: "warehouse_approve", module: "warehouse", action: "approve", label: "Approve Warehouse", description: "Approve warehouse requests", route: "/warehouse" },

  // Accounting additional
  accounting_delete: { key: "accounting_delete", module: "accounting", action: "delete", label: "Delete Accounting", description: "Delete accounting records", route: "/accounting" },
  accounting_approve: { key: "accounting_approve", module: "accounting", action: "approve", label: "Approve Accounting", description: "Approve accounting entries", route: "/accounting" },

  // Reports additional
  reports_create: { key: "reports_create", module: "reports", action: "create", label: "Create Reports", description: "Create custom reports", route: "/reports" },
  reports_edit: { key: "reports_edit", module: "reports", action: "edit", label: "Edit Reports", description: "Edit reports", route: "/reports" },
  reports_delete: { key: "reports_delete", module: "reports", action: "delete", label: "Delete Reports", description: "Delete reports", route: "/reports" },

  // Settings additional
  settings_create: { key: "settings_create", module: "settings", action: "create", label: "Create Settings", description: "Create settings entries", route: "/settings" },

  // Admin additional
  admin_create: { key: "admin_create", module: "admin", action: "create", label: "Create Admin", description: "Create admin records", route: "/system-audit" },

  // Contractors Module
  contractors_view: { key: "contractors_view", module: "contractors", action: "view", label: "View Contractors", description: "View contractor list and details", route: "/contractors" },
  contractors_create: { key: "contractors_create", module: "contractors", action: "create", label: "Create Contractors", description: "Create new contractors", route: "/contractor-onboarding" },
  contractors_edit: { key: "contractors_edit", module: "contractors", action: "edit", label: "Edit Contractors", description: "Edit contractor information", route: "/contractors/:id" },
  contractors_delete: { key: "contractors_delete", module: "contractors", action: "delete", label: "Delete Contractors", description: "Delete contractors", route: "/contractors" },
  contractors_approve: { key: "contractors_approve", module: "contractors", action: "approve", label: "Approve Contractors", description: "Approve contractor onboarding", route: "/contractor-intake" },

  // Inventory Module
  inventory_view: { key: "inventory_view", module: "inventory", action: "view", label: "View Inventory", description: "View inventory list", route: "/inventory" },
  inventory_create: { key: "inventory_create", module: "inventory", action: "create", label: "Create Inventory", description: "Add inventory items", route: "/inventory" },
  inventory_edit: { key: "inventory_edit", module: "inventory", action: "edit", label: "Edit Inventory", description: "Edit inventory items", route: "/inventory" },
  inventory_delete: { key: "inventory_delete", module: "inventory", action: "delete", label: "Delete Inventory", description: "Delete inventory items", route: "/inventory" },

  // Warehouse Module
  warehouse_view: { key: "warehouse_view", module: "warehouse", action: "view", label: "View Warehouse", description: "View warehouse operations", route: "/warehouse" },
  warehouse_create: { key: "warehouse_create", module: "warehouse", action: "create", label: "Create Warehouse", description: "Create warehouse records", route: "/warehouse" },
  warehouse_edit: { key: "warehouse_edit", module: "warehouse", action: "edit", label: "Edit Warehouse", description: "Edit warehouse operations", route: "/warehouse" },
  warehouse_delete: { key: "warehouse_delete", module: "warehouse", action: "delete", label: "Delete Warehouse", description: "Delete warehouse records", route: "/warehouse" },

  // Ordering Module
  ordering_view: { key: "ordering_view", module: "ordering", action: "view", label: "View Ordering", description: "View ordering queue", route: "/ordering-queue" },
  ordering_create: { key: "ordering_create", module: "ordering", action: "create", label: "Create Orders", description: "Create purchase orders", route: "/ordering-queue" },
  ordering_edit: { key: "ordering_edit", module: "ordering", action: "edit", label: "Edit Orders", description: "Edit purchase orders", route: "/ordering-queue" },
  ordering_delete: { key: "ordering_delete", module: "ordering", action: "delete", label: "Delete Orders", description: "Delete purchase orders", route: "/ordering-queue" },
  ordering_approve: { key: "ordering_approve", module: "ordering", action: "approve", label: "Approve Orders", description: "Approve purchase orders", route: "/ordering-queue" },

  // Permitting Module
  permitting_view: { key: "permitting_view", module: "permitting", action: "view", label: "View Permitting", description: "View permit tracker", route: "/permitting" },
  permitting_create: { key: "permitting_create", module: "permitting", action: "create", label: "Create Permits", description: "Create permit requests", route: "/permitting" },
  permitting_edit: { key: "permitting_edit", module: "permitting", action: "edit", label: "Edit Permits", description: "Edit permit requests", route: "/permitting" },
  permitting_approve: { key: "permitting_approve", module: "permitting", action: "approve", label: "Approve Permits", description: "Approve permits", route: "/permitting" },

  // Issues Module
  issues_view: { key: "issues_view", module: "issues", action: "view", label: "View Issues", description: "View issue list", route: "/issues" },
  issues_create: { key: "issues_create", module: "issues", action: "create", label: "Create Issues", description: "Create issues", route: "/issue-form" },
  issues_edit: { key: "issues_edit", module: "issues", action: "edit", label: "Edit Issues", description: "Edit issues", route: "/issues/:id" },
  issues_delete: { key: "issues_delete", module: "issues", action: "delete", label: "Delete Issues", description: "Delete issues", route: "/issues" },

  // Accounting Module
  accounting_view: { key: "accounting_view", module: "accounting", action: "view", label: "View Accounting", description: "View accounting data", route: "/accounting" },
  accounting_create: { key: "accounting_create", module: "accounting", action: "create", label: "Create Accounting", description: "Create accounting records", route: "/accounting" },
  accounting_edit: { key: "accounting_edit", module: "accounting", action: "edit", label: "Edit Accounting", description: "Edit accounting records", route: "/accounting" },
  accounting_export: { key: "accounting_export", module: "accounting", action: "export", label: "Export Accounting", description: "Export accounting data", route: "/accounting" },

  // Reports Module
  reports_view: { key: "reports_view", module: "reports", action: "view", label: "View Reports", description: "View reports", route: "/reports" },
  reports_export: { key: "reports_export", module: "reports", action: "export", label: "Export Reports", description: "Export report data", route: "/reports" },

  // Settings Module
  settings_view: { key: "settings_view", module: "settings", action: "view", label: "View Settings", description: "View system settings", route: "/settings" },
  settings_edit: { key: "settings_edit", module: "settings", action: "edit", label: "Edit Settings", description: "Edit system settings", route: "/settings" },

  // Admin Module
  admin_view: { key: "admin_view", module: "admin", action: "view", label: "View Admin", description: "View admin console", route: "/system-audit" },
  admin_edit: { key: "admin_edit", module: "admin", action: "edit", label: "Edit Admin", description: "Edit admin settings", route: "/system-audit" },

  // Catalog Module
  catalog_view: { key: "catalog_view", module: "catalog", action: "view", label: "View Catalog", description: "View offerings catalog", route: "/offerings" },
  catalog_create: { key: "catalog_create", module: "catalog", action: "create", label: "Create Catalog", description: "Create catalog items", route: "/offerings" },
  catalog_edit: { key: "catalog_edit", module: "catalog", action: "edit", label: "Edit Catalog", description: "Edit catalog items", route: "/offerings" },
  catalog_delete: { key: "catalog_delete", module: "catalog", action: "delete", label: "Delete Catalog", description: "Delete catalog items", route: "/offerings" },

  // Vendors Module
  vendors_view: { key: "vendors_view", module: "vendors", action: "view", label: "View Vendors", description: "View vendor list", route: "/vendors" },
  vendors_create: { key: "vendors_create", module: "vendors", action: "create", label: "Create Vendors", description: "Create vendor records", route: "/vendors" },
  vendors_edit: { key: "vendors_edit", module: "vendors", action: "edit", label: "Edit Vendors", description: "Edit vendor records", route: "/vendors" },
  vendors_delete: { key: "vendors_delete", module: "vendors", action: "delete", label: "Delete Vendors", description: "Delete vendor records", route: "/vendors" },

  // Messages Module
  messages_view: { key: "messages_view", module: "messages", action: "view", label: "View Messages", description: "View messages", route: "/messages" },
  messages_create: { key: "messages_create", module: "messages", action: "create", label: "Create Messages", description: "Send messages", route: "/messages" },
  messages_delete: { key: "messages_delete", module: "messages", action: "delete", label: "Delete Messages", description: "Delete messages", route: "/messages" },

  // Automations Module
  automations_view: { key: "automations_view", module: "automations", action: "view", label: "View Automations", description: "View automations", route: "/automations" },
  automations_create: { key: "automations_create", module: "automations", action: "create", label: "Create Automations", description: "Create automations", route: "/automations" },
  automations_edit: { key: "automations_edit", module: "automations", action: "edit", label: "Edit Automations", description: "Edit automations", route: "/automations" },
  automations_delete: { key: "automations_delete", module: "automations", action: "delete", label: "Delete Automations", description: "Delete automations", route: "/automations" },

  // QA Module
  qa_view: { key: "qa_view", module: "qa", action: "view", label: "View QA", description: "View QA forms", route: "/qa-form" },
  qa_create: { key: "qa_create", module: "qa", action: "create", label: "Create QA", description: "Create QA forms", route: "/qa-form" },
  qa_edit: { key: "qa_edit", module: "qa", action: "edit", label: "Edit QA", description: "Edit QA forms", route: "/qa-form" },
  qa_approve: { key: "qa_approve", module: "qa", action: "approve", label: "Approve QA", description: "Approve QA forms", route: "/qa-form" },

  // Installation Module
  installation_view: { key: "installation_view", module: "installation", action: "view", label: "View Installation", description: "View installation readiness", route: "/install-readiness" },
  installation_edit: { key: "installation_edit", module: "installation", action: "edit", label: "Edit Installation", description: "Edit installation readiness", route: "/install-readiness" },
  installation_approve: { key: "installation_approve", module: "installation", action: "approve", label: "Approve Installation", description: "Approve installation readiness", route: "/install-readiness" },

  // Scheduling Module
  scheduling_view: { key: "scheduling_view", module: "scheduling", action: "view", label: "View Scheduling", description: "View scheduling calendar", route: "/scheduling" },
  scheduling_create: { key: "scheduling_create", module: "scheduling", action: "create", label: "Create Scheduling", description: "Create schedule entries", route: "/scheduling" },
  scheduling_edit: { key: "scheduling_edit", module: "scheduling", action: "edit", label: "Edit Scheduling", description: "Edit schedule entries", route: "/scheduling" },

  // Customers additional permissions
  customers_approve: { key: "customers_approve", module: "customers", action: "approve", label: "Approve Customers", description: "Approve customer requests", route: "/customers" },

  // Leads additional
  leads_approve: { key: "leads_approve", module: "leads", action: "approve", label: "Approve Leads", description: "Approve lead conversions", route: "/pipeline" },

  // Jobs additional
  jobs_export: { key: "jobs_export", module: "jobs", action: "export", label: "Export Jobs", description: "Export job data", route: "/jobs" },

  // Opportunities additional
  opportunities_export: { key: "opportunities_export", module: "opportunities", action: "export", label: "Export Opportunities", description: "Export opportunity data", route: "/opportunities" },

  // Contractors additional
  contractors_export: { key: "contractors_export", module: "contractors", action: "export", label: "Export Contractors", description: "Export contractor data", route: "/contractors" },

  // Inventory additional
  inventory_export: { key: "inventory_export", module: "inventory", action: "export", label: "Export Inventory", description: "Export inventory data", route: "/inventory" },

  // Warehouse additional
  warehouse_export: { key: "warehouse_export", module: "warehouse", action: "export", label: "Export Warehouse", description: "Export warehouse data", route: "/warehouse" },

  // Ordering additional
  ordering_export: { key: "ordering_export", module: "ordering", action: "export", label: "Export Ordering", description: "Export ordering data", route: "/ordering-queue" },

  // Permitting additional
  permitting_export: { key: "permitting_export", module: "permitting", action: "export", label: "Export Permits", description: "Export permit data", route: "/permitting" },
  permitting_delete: { key: "permitting_delete", module: "permitting", action: "delete", label: "Delete Permits", description: "Delete permits", route: "/permitting" },

  // Issues additional
  issues_approve: { key: "issues_approve", module: "issues", action: "approve", label: "Approve Issues", description: "Approve issue resolutions", route: "/issues" },
  issues_export: { key: "issues_export", module: "issues", action: "export", label: "Export Issues", description: "Export issue data", route: "/issues" },

  // QA additional
  qa_delete: { key: "qa_delete", module: "qa", action: "delete", label: "Delete QA", description: "Delete QA forms", route: "/qa-form" },
  qa_export: { key: "qa_export", module: "qa", action: "export", label: "Export QA", description: "Export QA data", route: "/qa-form" },

  // Installation additional
  installation_delete: { key: "installation_delete", module: "installation", action: "delete", label: "Delete Installation", description: "Delete installation records", route: "/install-readiness" },
  installation_export: { key: "installation_export", module: "installation", action: "export", label: "Export Installation", description: "Export installation data", route: "/install-readiness" },
  installation_create: { key: "installation_create", module: "installation", action: "create", label: "Create Installation", description: "Create installation records", route: "/install-readiness" },

  // Scheduling additional
  scheduling_delete: { key: "scheduling_delete", module: "scheduling", action: "delete", label: "Delete Scheduling", description: "Delete schedule entries", route: "/scheduling" },
  scheduling_approve: { key: "scheduling_approve", module: "scheduling", action: "approve", label: "Approve Scheduling", description: "Approve scheduling entries", route: "/scheduling" },

  // Vendors additional
  vendors_approve: { key: "vendors_approve", module: "vendors", action: "approve", label: "Approve Vendors", description: "Approve vendor requests", route: "/vendors" },
  vendors_export: { key: "vendors_export", module: "vendors", action: "export", label: "Export Vendors", description: "Export vendor data", route: "/vendors" },

  // Messages additional
  messages_edit: { key: "messages_edit", module: "messages", action: "edit", label: "Edit Messages", description: "Edit messages", route: "/messages" },
  messages_export: { key: "messages_export", module: "messages", action: "export", label: "Export Messages", description: "Export messages", route: "/messages" },

  // Automations additional
  automations_approve: { key: "automations_approve", module: "automations", action: "approve", label: "Approve Automations", description: "Approve automation rules", route: "/automations" },
  automations_export: { key: "automations_export", module: "automations", action: "export", label: "Export Automations", description: "Export automation data", route: "/automations" },

  // Settings additional
  settings_delete: { key: "settings_delete", module: "settings", action: "delete", label: "Delete Settings", description: "Delete settings", route: "/settings" },
  settings_export: { key: "settings_export", module: "settings", action: "export", label: "Export Settings", description: "Export settings", route: "/settings" },
  settings_approve: { key: "settings_approve", module: "settings", action: "approve", label: "Approve Settings", description: "Approve settings changes", route: "/settings" },

  // Admin additional
  admin_delete: { key: "admin_delete", module: "admin", action: "delete", label: "Delete Admin", description: "Delete admin records", route: "/system-audit" },
  admin_export: { key: "admin_export", module: "admin", action: "export", label: "Export Admin", description: "Export admin data", route: "/system-audit" },
  admin_approve: { key: "admin_approve", module: "admin", action: "approve", label: "Approve Admin", description: "Approve admin actions", route: "/system-audit" },

  // Geo Tracking Module
  geo_tracking_view: { key: "geo_tracking_view", module: "geo_tracking", action: "view", label: "View Geo Tracking", description: "View installer geolocation events and deviation alerts" },

  // Catalog additional
  catalog_approve: { key: "catalog_approve", module: "catalog", action: "approve", label: "Approve Catalog", description: "Approve catalog items", route: "/offerings" },
  catalog_export: { key: "catalog_export", module: "catalog", action: "export", label: "Export Catalog", description: "Export catalog data", route: "/offerings" },
};

/**
 * Role permission assignments - maps each role to allowed permissions
 * This is the centralized permission table
 */
export const ROLE_PERMISSIONS: Record<UserRole, PermissionKey[]> = {
  super_admin: Object.keys(PERMISSIONS) as PermissionKey[], // All permissions
  owner: [
    "leads_view", "leads_export",
    "opportunities_view", "opportunities_create", "opportunities_edit", "opportunities_approve",
    "jobs_view", "jobs_edit",
    "customers_view", "customers_create", "customers_edit",
    "contractors_view", "contractors_edit", "contractors_approve",
    "inventory_view",
    "warehouse_view",
    "ordering_view", "ordering_approve",
    "permitting_view", "permitting_edit", "permitting_approve",
    "issues_view", "issues_create", "issues_edit",
    "accounting_view", "accounting_export",
    "reports_view", "reports_export",
    "settings_view", "settings_edit",
    "admin_view", "admin_edit",
    "catalog_view", "catalog_create", "catalog_edit", "catalog_delete",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "messages_view", "messages_create", "messages_delete",
    "automations_view", "automations_create", "automations_edit", "automations_delete",
    "qa_view", "qa_create", "qa_edit", "qa_approve",
    "installation_view", "installation_edit", "installation_approve",
    "geo_tracking_view",
  ],
  cfo: [
    "leads_view",
    "opportunities_view",
    "jobs_view",
    "customers_view",
    "contractors_view",
    "inventory_view",
    "warehouse_view",
    "ordering_view", "ordering_approve",
    "issues_view",
    "accounting_view", "accounting_create", "accounting_edit", "accounting_export",
    "reports_view", "reports_export",
  ],
  finance_manager: [
    "leads_view",
    "opportunities_view",
    "jobs_view",
    "customers_view",
    "contractors_view",
    "inventory_view",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
    "issues_view",
    "accounting_view", "accounting_create", "accounting_edit", "accounting_export",
    "reports_view", "reports_export",
  ],
  bookkeeper: [
    "leads_view",
    "opportunities_view",
    "jobs_view",
    "customers_view",
    "issues_view",
    "accounting_view", "accounting_create", "accounting_edit",
    "reports_view", "reports_export",
  ],
  ops_manager: [
    "leads_view", "leads_create", "leads_edit", "leads_export",
    "opportunities_view", "opportunities_create", "opportunities_edit", "opportunities_approve",
    "jobs_view", "jobs_create", "jobs_edit", "jobs_approve",
    "customers_view", "customers_create", "customers_edit",
    "contractors_view", "contractors_create", "contractors_edit", "contractors_approve",
    "inventory_view", "inventory_create", "inventory_edit",
    "warehouse_view", "warehouse_create", "warehouse_edit",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
    "permitting_view", "permitting_create", "permitting_edit", "permitting_approve",
    "issues_view", "issues_create", "issues_edit", "issues_delete",
    "accounting_view", "accounting_export",
    "reports_view", "reports_export",
    "catalog_view", "catalog_create", "catalog_edit",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "messages_view", "messages_create", "messages_delete",
    "automations_view", "automations_create", "automations_edit",
    "qa_view", "qa_create", "qa_edit", "qa_approve",
    "installation_view", "installation_edit", "installation_approve",
    "geo_tracking_view",
  ],
  admin: [
    "leads_view", "leads_create", "leads_edit",
    "opportunities_view", "opportunities_create", "opportunities_edit",
    "jobs_view", "jobs_create", "jobs_edit",
    "customers_view", "customers_create", "customers_edit",
    "contractors_view", "contractors_create", "contractors_edit", "contractors_approve",
    "inventory_view", "inventory_create", "inventory_edit",
    "warehouse_view", "warehouse_create", "warehouse_edit",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
    "permitting_view", "permitting_create", "permitting_edit", "permitting_approve",
    "issues_view", "issues_create", "issues_edit", "issues_delete",
    "accounting_view", "accounting_export",
    "reports_view", "reports_export",
    "settings_view", "settings_edit",
    "admin_view", "admin_edit",
    "catalog_view", "catalog_create", "catalog_edit", "catalog_delete",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "messages_view", "messages_create", "messages_delete",
    "automations_view", "automations_create", "automations_edit", "automations_delete",
    "qa_view", "qa_create", "qa_edit", "qa_approve",
    "installation_view", "installation_edit", "installation_approve",
    "geo_tracking_view",
  ],
  it_staff: [
    "leads_view", "leads_create", "leads_edit",
    "opportunities_view", "opportunities_create", "opportunities_edit",
    "jobs_view", "jobs_create", "jobs_edit",
    "customers_view", "customers_create", "customers_edit",
    "contractors_view", "contractors_create", "contractors_edit", "contractors_approve",
    "inventory_view", "inventory_create", "inventory_edit",
    "warehouse_view", "warehouse_create", "warehouse_edit",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
    "permitting_view", "permitting_create", "permitting_edit", "permitting_approve",
    "issues_view", "issues_create", "issues_edit", "issues_delete",
    "accounting_view", "accounting_export",
    "reports_view", "reports_export",
    "settings_view", "settings_edit",
    "admin_view", "admin_edit",
    "catalog_view", "catalog_create", "catalog_edit", "catalog_delete",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "messages_view", "messages_create", "messages_delete",
    "automations_view", "automations_create", "automations_edit", "automations_delete",
    "qa_view", "qa_create", "qa_edit", "qa_approve",
    "installation_view", "installation_edit", "installation_approve",
    "geo_tracking_view",
  ],
  warehouse_manager: [
    "jobs_view", "jobs_edit",
    "inventory_view", "inventory_create", "inventory_edit", "inventory_delete",
    "warehouse_view", "warehouse_create", "warehouse_edit", "warehouse_delete",
    "issues_view", "issues_create", "issues_edit",
  ],
  head_of_installation: [
    "jobs_view", "jobs_edit",
    "inventory_view",
    "warehouse_view",
    "ordering_view",
    "installation_view", "installation_edit", "installation_approve",
    "qa_view", "qa_create", "qa_edit", "qa_approve",
    "contractors_view", "contractors_edit",
    "scheduling_view",
    "issues_view", "issues_create", "issues_edit",
    "geo_tracking_view",
  ],
  procurement_manager: [
    "jobs_view",
    "inventory_view", "inventory_create", "inventory_edit",
    "warehouse_view", "warehouse_create", "warehouse_edit",
    "ordering_view", "ordering_create", "ordering_edit", "ordering_approve",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "issues_view", "issues_create", "issues_edit",
  ],
  inventory_manager: [
    "jobs_view",
    "inventory_view", "inventory_create", "inventory_edit", "inventory_delete",
    "warehouse_view", "warehouse_create", "warehouse_edit",
    "vendors_view", "vendors_create", "vendors_edit", "vendors_delete",
    "issues_view", "issues_create", "issues_edit",
  ],
  installer: [
    "jobs_view", "jobs_edit",
    "qa_view", "qa_create", "qa_edit",
    "issues_view", "issues_create", "issues_edit",
  ],
  subcontractor: [
    "jobs_view", "jobs_edit",
    "opportunities_view",
    "qa_view", "qa_create", "qa_edit",
    "issues_view", "issues_create", "issues_edit",
  ],
  sales: [
    "leads_view", "leads_create", "leads_edit",
    "opportunities_view", "opportunities_create", "opportunities_edit",
    "jobs_view",
    "customers_view", "customers_create", "customers_edit",
    "messages_view", "messages_create",
    "reports_view",
    "issues_view", "issues_create", "issues_edit",
  ],
  sales_manager: [
    "leads_view", "leads_export",
    "opportunities_view", "opportunities_approve", "opportunities_export",
    "jobs_view", "jobs_export",
    "customers_view",
    "contractors_view",
    "messages_view", "messages_create",
    "reports_view", "reports_export",
    "accounting_view",
    "scheduling_view",
    "automations_view",
    "issues_view", "issues_create",
  ],
  call_center: [
    "leads_view", "leads_create", "leads_edit",
    "opportunities_view",
    "customers_view", "customers_create", "customers_edit",
    "messages_view", "messages_create",
    "scheduling_view", "scheduling_create", "scheduling_edit",
    "issues_view", "issues_create",
    "automations_view",
  ],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: PermissionKey): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the given permissions
 */
export function hasAnyPermission(role: UserRole, permissions: PermissionKey[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

/**
 * Check if a role has all of the given permissions
 */
export function hasAllPermissions(role: UserRole, permissions: PermissionKey[]): boolean {
  return permissions.every((p) => hasPermission(role, p));
}

/**
 * Get all permissions for a role grouped by module
 */
export function getPermissionsByModule(role: UserRole): Record<PermissionModule, PermissionKey[]> {
  const rolePerms = ROLE_PERMISSIONS[role] ?? [];
  const grouped: Record<PermissionModule, PermissionKey[]> = {} as Record<PermissionModule, PermissionKey[]>;

  for (const perm of rolePerms) {
    const def = PERMISSIONS[perm];
    if (def) {
      if (!grouped[def.module]) {
        grouped[def.module] = [];
      }
      grouped[def.module].push(perm);
    }
  }

  return grouped;
}

/**
 * Get all unique modules that a role has any permission for
 */
export function getAllowedModules(role: UserRole): PermissionModule[] {
  const byModule = getPermissionsByModule(role);
  return Object.keys(byModule) as PermissionModule[];
}